//@flow
import React from 'react';
import { FadeLoader } from 'react-spinners';
import { connect } from 'react-redux';
import { isMobile, isIOS } from 'react-device-detect';
import {
  setAuthStatus,
  setUserName,
  setUserData,
  setCreditCardData,
  setScheduleData,
  setExercisesData,
} from '../../redux/actionCreators';
import Api from '../../api/api';
import { getJsonFromUrl, getUrlFromJson } from '../../utils/helpers';
import { Grid, Panel, PanelGroup, Alert, Glyphicon } from 'react-bootstrap';
import type { UserType, CreditCardType, ScheduleType, ExerciseType } from '../../utils/types';
import Exercises from './Exercises/Exercises';
import Profile from './Profile/Profile';
import Schedule from './Schedule/Schedule';
import ChooseAppPlatformModal from './Profile/ChooseAppPlatformModal';

type Props = {
  location: Object,
  history: Object,
  user: UserType,
  handleChangeAuthStatus: (authenticated: boolean) => void,
  setUserName: (userName: string) => void,
  setUserData: (user: UserType) => void,
  setCreditCardData: (card: CreditCardType) => void,
  setScheduleData: (schedule: ScheduleType) => void,
  setExercisesData: (exercises: Array<ExerciseType>) => void,
};

type State = {
  showInfo: boolean,
  showSchedule: boolean,
  showExercises: boolean,
  message: {
    type: string,
    text: string,
  },
  loading: boolean,
  paymentError: boolean,
  showSuspendSubscriptionModal: boolean,
  showMobileAppModal: boolean,
  showChooseAppPlatformModal: boolean,
  authToken: string,
};

class Dashboard extends React.Component<Props, State> {
  state = {
    showInfo: false,
    showSchedule: true,
    showExercises: true,
    message: {
      text: '',
      type: 'success',
    },
    loading: false,
    paymentError: false,
    showSuspendSubscriptionModal: false,
    showMobileAppModal: false,
    showChooseAppPlatformModal: false,
    authToken: '',
  };

  componentDidMount = async () => {
    //$FlowFixMe
    document.body.classList.add('profile');

    const { search } = this.props.location;

    if (!search) {
      return this.fetchData();
    }

    const requestData = getJsonFromUrl(search);

    if (requestData.confirmation_token) {
      await this.checkToken(requestData.confirmation_token);
    }

    if (requestData['show-suspend-modal'] && requestData['show-suspend-modal'] === 'true') {
      this.setState({ showSuspendSubscriptionModal: true });

      this.clearRouterHistorySearch();
    }

    if (requestData.mobile && requestData.mobile === 'true' && !this.state.showChooseAppPlatformModal) {
      this.setState({ showMobileAppModal: true });
    }

    this.fetchData();
  };

  componentDidUpdate(prevProps, prevState): void {
    const alert = document.getElementById('dashboardAlert');
    const message = this.state.message;

    if (alert && message.type === 'success') {
      setTimeout((): void => {
        alert.style.display = 'none';
        this.setMessage();
      }, 5000);
    }
  }

  componentWillUnmount() {
    document.body && document.body.classList.remove('profile');
  }

  closeSuspendSubscriptionModal = () => this.setState({ showSuspendSubscriptionModal: false });

  closeMobileAppModal = () => this.setState({ showMobileAppModal: false });

  handleChooseAppPlatformModalClose = () => {
    this.setState({ showChooseAppPlatformModal: false });

    this.clearRouterHistorySearch();
  };

  clearRouterHistorySearch = () =>
    this.props.history.push({
      pathname: '/profile',
      search: '',
    });

  checkToken = confirmation_token => {
    this.setState({ loading: true });

    return Api.users
      .verifyEmail(confirmation_token)
      .then((user: UserType): void => {
        this.props.handleChangeAuthStatus(true);

        this.props.setUserName(`${user.first_name} ${user.last_name}`);

        this.setState({
          loading: false,
          authToken: user.authentication_token,
          showChooseAppPlatformModal: isMobile && isIOS,
        });

        this.clearRouterHistorySearch();
      })
      .catch((error): void => {
        if (error.response.status === 422) {
          this.setState({ loading: false });

          this.props.history.push('/invalid-link');
        }
      });
  };

  handleOpenInAppClick = () => {
    this.handleChooseAppPlatformModalClose();

    window.location.assign(
      `${process.env.REACT_APP_DEEPLINK_HOST}/profile?authentication_token=${this.state.authToken}`,
    );
  };

  definePanelsInitialState = ({ subscribed, payment_status }) => {
    if (!subscribed || payment_status === 'error') {
      this.setState({ showInfo: true, showSchedule: false, showExercises: false });
    }
  };

  fetchData = () => {
    this.fetchUserData();
    this.fetchCreditCardData();
    this.fetchExercises();
    this.fetchSchedule();
  };

  fetchUserData = (): void =>
    Api.users
      .getUserPersonalData()
      .then((user: UserType) => {
        this.props.setUserData(user);
        this.props.setUserName(`${user.first_name} ${user.last_name}`);
        this.definePanelsInitialState(user);

        const interval = this.state.message.text ? 5000 : 1;

        if (user.payment_status === 'error') {
          this.setState({ paymentError: true, showSuspendSubscriptionModal: false });

          setTimeout(
            (): void =>
              this.setMessage(
                'Warning. Unfortunately we were unable to charge your payment fee. Please review your Credit Card details and try again. You can update Credit Card information in the Account Information section on the dashboard.',
                'danger',
              ),
            interval,
          );
        }
      })
      .catch(error => error.response.status === 401 && this.props.history.push('/login'));

  fetchCreditCardData = (): void =>
    Api.users
      .getUserCreditCardData()
      .then((card: CreditCardType) => {
        this.props.setCreditCardData(card);
      })
      .catch(error => error.response.status === 401 && this.props.history.push('/login'));

  fetchSchedule = (): void =>
    Api.users
      .getSchedule()
      .then((schedule: ScheduleType) => this.props.setScheduleData(schedule))
      .catch(error => error.response.status === 401 && this.props.history.push('/login'));

  fetchExercises = (filter: string = ''): void =>
    Api.users
      .getExercises(filter)
      .then((exercises: Array<ExerciseType>) => this.props.setExercisesData(exercises))
      .catch(error => error.response.status === 401 && this.props.history.push('/login'));

  setMessage = (text: string = '', type?: string = 'success'): void => this.setState({ message: { text, type } });

  render() {
    const {
      showInfo,
      showSchedule,
      showExercises,
      message: { type, text },
      loading,
      paymentError,
      showSuspendSubscriptionModal,
      showMobileAppModal,
      showChooseAppPlatformModal,
    } = this.state;

    const { user } = this.props;

    const panelIsLocked = () => user.payment_status === 'error' || !user.subscribed;

    return (
      <Grid fluid className={`main-dashboard ${loading ? 'loading' : ''}`}>
        {text && (
          <Alert bsStyle={type} id="dashboardAlert" onDismiss={() => this.setMessage()}>
            <span className="png-ok-circle" />
            <span className="text-alert">{text}</span>
          </Alert>
        )}

        {loading && (
          <div className="loader-container">
            <FadeLoader className="loader" color={'#095674'} loading={loading} />
          </div>
        )}

        {/*<h1 className="main-dashboard-title">Account Information</h1>*/}
        <PanelGroup id="profileWrapperPanel">
          <Panel eventKey="1" expanded={showInfo}>
            <Panel.Heading onClick={() => this.setState({ showInfo: !showInfo })}>
              <div className="btn-group pull-right">
                <Glyphicon glyph={`chevron-${showInfo ? 'down' : 'right'}`} />
              </div>
              <Panel.Title toggle>Account Information</Panel.Title>
            </Panel.Heading>

            <Panel.Body collapsible>
              <Profile
                fetchUserData={this.fetchUserData}
                fetchCreditCardData={this.fetchCreditCardData}
                fetchExercises={this.fetchExercises}
                fetchSchedule={this.fetchSchedule}
                setMessage={this.setMessage}
                paymentError={paymentError}
                showSuspendSubscriptionModal={showSuspendSubscriptionModal}
                closeSuspendSubscriptionModal={this.closeSuspendSubscriptionModal}
                showMobileAppModal={showMobileAppModal}
                closeMobileAppModal={this.closeMobileAppModal}
              />
            </Panel.Body>
          </Panel>

          <Panel eventKey="2" expanded={showSchedule}>
            <Panel.Heading onClick={() => !panelIsLocked() && this.setState({ showSchedule: !showSchedule })}>
              <div className="btn-group pull-right">
                <Glyphicon glyph={`${panelIsLocked() ? 'lock' : showSchedule ? 'chevron-down' : 'chevron-right'}`} />
              </div>
              <Panel.Title toggle>Subscription Method, Scheduling</Panel.Title>
            </Panel.Heading>
            <Panel.Body collapsible>
              <Schedule
                fetchSchedule={this.fetchSchedule}
                fetchUserData={this.fetchUserData}
                setMessage={this.setMessage}
              />
            </Panel.Body>
          </Panel>

          <Panel eventKey="3" expanded={showExercises}>
            <Panel.Heading onClick={() => !panelIsLocked() && this.setState({ showExercises: !showExercises })}>
              <div className="btn-group pull-right">
                <Glyphicon glyph={`${panelIsLocked() ? 'lock' : showExercises ? 'chevron-down' : 'chevron-right'}`} />
              </div>
              <Panel.Title toggle>Exercises</Panel.Title>
            </Panel.Heading>
            <Panel.Body collapsible>
              <Exercises fetchExercises={this.fetchExercises} />
            </Panel.Body>
          </Panel>
        </PanelGroup>
        <ChooseAppPlatformModal
          show={showChooseAppPlatformModal}
          onHide={this.handleChooseAppPlatformModalClose}
          handleOpenInAppClick={this.handleOpenInAppClick}
        />
      </Grid>
    );
  }
}

const mapStateToProps = state => ({ user: state.user });

const mapDispatchToProps = (dispatch: Function) => ({
  handleChangeAuthStatus(authenticated) {
    dispatch(setAuthStatus(authenticated));
  },

  setUserName(userName) {
    dispatch(setUserName(userName));
  },

  setUserData(user) {
    dispatch(setUserData(user));
  },

  setCreditCardData(card) {
    dispatch(setCreditCardData(card));
  },

  setScheduleData(schedule) {
    dispatch(setScheduleData(schedule));
  },

  setExercisesData(exercises) {
    dispatch(setExercisesData(exercises));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Dashboard);



// WEBPACK FOOTER //
// ./src/components/Dashboard/Dashboard.js